import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaPlus, FaClone, FaCheckCircle, FaTimesCircle, FaSlidersH, FaUsers, FaTrash, FaUserShield, FaDatabase, FaStickyNote } from "react-icons/fa";
import { getRoles, createRole, cloneRole, toggleRoleStatus, deleteRole } from "../services/rbacservice";
import RoleMatrixModal from "./role_matrix_modal";
import RoleUsersModal from "./role_users_modal";
import AuditLogsModal from "./audit_logs_modal";
import ConfirmModal from "../employee/delete_modal";
import Can from "../common/can";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import { parseFieldErrors } from "../../utils/errors";
import "./role_management.css";

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleForMatrix, setSelectedRoleForMatrix] = useState(null);
  const [selectedRoleForUsers, setSelectedRoleForUsers] = useState(null);
  const [deleteTargetRole, setDeleteTargetRole] = useState(null);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [roleScopeFilter, setRoleScopeFilter] = useState("all");
  const [rolePage, setRolePage] = useState(1);
  const ROLE_PAGE_SIZE = 6;

  // Create / Clone Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(null); // holds role object to clone
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formScope, setFormScope] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await getRoles();
      setRoles(data || []);
    } catch (err) {
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFieldErrors({ name: "Role name is required" });
      return;
    }
    setSubmitting(true);
    setFieldErrors({});
    try {
      await createRole({
        name: formName.trim().toLowerCase(),
        description: formDesc.trim(),
        data_scope: formScope,
        is_active: true,
      });
      toast.success(`Role '${formName}' created successfully`);
      setShowCreateModal(false);
      resetForm();
      loadRoles();
    } catch (err) {
      setFieldErrors(parseFieldErrors(err));
      toast.error(err.response?.data?.message || "Failed to create role");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloneRole = async (e) => {
    e.preventDefault();
    if (!formName.trim() || !showCloneModal) {
      if (!formName.trim()) setFieldErrors({ name: "New role name is required" });
      return;
    }
    setSubmitting(true);
    setFieldErrors({});
    try {
      await cloneRole(showCloneModal.id, formName.trim().toLowerCase(), formDesc.trim());
      toast.success(`Role cloned successfully as '${formName}'`);
      setShowCloneModal(null);
      resetForm();
      loadRoles();
    } catch (err) {
      setFieldErrors(parseFieldErrors(err));
      toast.error(err.response?.data?.message || "Failed to clone role");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (role) => {
    if (role.is_system_role && role.is_active) {
      toast.warning("System roles cannot be disabled.");
      return;
    }
    try {
      await toggleRoleStatus(role.id, !role.is_active);
      toast.success(`Role '${role.name}' is now ${!role.is_active ? "active" : "inactive"}`);
      loadRoles();
    } catch (err) {
      toast.error("Failed to update role status");
    }
  };

  const resetForm = () => {
    setFormName("");
    setFormDesc("");
    setFormScope(1);
    setFieldErrors({});
  };

  const filteredRoles = roles.filter((role) => {
    if (roleScopeFilter !== "all") {
      const scopeMatches = Number(role.data_scope) === Number(roleScopeFilter);
      if (!scopeMatches) return false;
    }
    if (!roleSearch.trim()) return true;
    const q = roleSearch.toLowerCase();
    return role.name.toLowerCase().includes(q) || (role.description && role.description.toLowerCase().includes(q));
  });

  const totalRolePages = Math.ceil(filteredRoles.length / ROLE_PAGE_SIZE) || 1;
  const paginatedRoles = filteredRoles.slice((rolePage - 1) * ROLE_PAGE_SIZE, rolePage * ROLE_PAGE_SIZE);

  return (
    <div className="rbac-container">
      <div className="rbac-header">
        <div className="rbac-heading-content">
          <h2>Enterprise Role &amp; Permission Management</h2>
          <p>Manage access control policies, data visibility scopes, and module permissions.</p>
        </div>
        <div className="rbac-header-actions">
          {/* <Can perm="audit_log.view">
            <button
              className="btn-secondary"
              onClick={() => setShowAuditLogs(true)}
              style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", padding: "10px 16px", background: "#f8fafc", border: "1.5px solid #cbd5e1", color: "#1e293b", borderRadius: "8px" }}
            >
              <FaHistory color="#2563eb" /> Security Audit Logs
            </button>
          </Can> */}
          <Can perm="role.create">
            <button
              className="primary-btn"
              onClick={() => { resetForm(); setShowCreateModal(true); }}
            >
              <FaPlus /> New Custom Role
            </button>
          </Can>
        </div>
      </div>

      <div className="rbac-filters-card">
        <div className="rbac-search-wrap">
          <input
            className="search-input"
            type="text"
            placeholder="🔍 Search by role name or description"
            value={roleSearch}
            onChange={(e) => {
              setRoleSearch(e.target.value);
              setRolePage(1);
            }}
          />
        </div>

        <div className="rbac-scope-filter">
          <Dropdown
            options={[
              { label: "All Data Scopes", value: "all" },
              { label: "Scope: All Data", value: "all_data" },
              { label: "Scope: Department", value: "department" },
              { label: "Scope: Reportees", value: "reportees" },
              { label: "Scope: Self Only", value: "self" },
            ]}
            value={roleScopeFilter}
            onChange={(value) => {
              setRoleScopeFilter(value);
              setRolePage(1);
            }}
            placeholder="Data Scope"
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}>
          Loading roles and permissions...
        </div>
      ) : filteredRoles.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#64748b", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px dashed #cbd5e1" }}>
          <p style={{ margin: 0, fontWeight: "500", fontSize: "16px" }}>No roles found matching criteria</p>
        </div>
      ) : (
        <>
          <div className="roles-grid">
            {paginatedRoles.map((role) => (
              <div key={role.id} className="role-card">
                <div>
                  <div className="role-card-header">
                    <h3 className="role-title">{role.name}</h3>
                    <div className="role-badges">
                      <span className={`badge ${role.is_system_role ? "badge-system" : "badge-custom"}`}>
                        {role.is_system_role ? "System Role" : "Custom"}
                      </span>
                      <span className="badge badge-scope">
                        Scope: {
                          Number(role.data_scope) === 1 ? "Self" :
                          Number(role.data_scope) === 2 ? "Reportees" :
                          Number(role.data_scope) === 3 ? "Department" :
                          Number(role.data_scope) === 4 ? "All" : role.data_scope
                        }
                      </span>
                    </div>
                  </div>

                  <p className="role-desc">
                    {role.description || "No description provided."}
                  </p>

                  <div className="role-stats">
                    <div
                      className="stat-item stat-users"
                      onClick={() => setSelectedRoleForUsers(role)}
                      title="Click to assign or view users for this role"
                    >
                      <span className="stat-val">{role.user_count || 0}</span>
                      <span className="stat-label">👥 Assigned Users</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-val">{role.permission_count || 0}</span>
                      <span className="stat-label">Permissions</span>
                    </div>
                  </div>
                </div>

                <div className="role-actions">
                  <Can perm="role.create">
                    <button
                      className="btn-secondary role-icon-btn"
                      title="Clone Role"
                      onClick={() => { resetForm(); setFormName(`${role.name}_copy`); setShowCloneModal(role); }}
                    >
                      <FaClone />
                    </button>
                  </Can>

                  {!role.is_system_role && (
                    <>
                      <Can perm="role.update">
                        <button
                          className="btn-secondary role-icon-btn"
                          title={role.is_active ? "Disable Role" : "Enable Role"}
                          onClick={() => handleToggleStatus(role)}
                          style={{ color: role.is_active ? "#059669" : "#dc2626" }}
                        >
                          {role.is_active ? <FaCheckCircle /> : <FaTimesCircle />}
                        </button>
                      </Can>
                      <Can perm="role.delete">
                        <button
                          className="btn-secondary role-icon-btn"
                          title="Delete Role"
                          onClick={() => setDeleteTargetRole(role)}
                          style={{ color: "#dc2626" }}
                        >
                          <FaTrash />
                        </button>
                      </Can>
                    </>
                  )}

                  <Can perm="role.update">
                    <button
                      className="btn-assign-users"
                      onClick={() => setSelectedRoleForUsers(role)}
                    >
                      <FaUsers /> Assign Users
                    </button>
                  </Can>

                  <Can perm="role.update">
                    <button
                      className="btn-primary btn-edit-permissions"
                      onClick={() => setSelectedRoleForMatrix(role)}
                    >
                      <FaSlidersH /> Edit Permissions
                    </button>
                  </Can>
                </div>
              </div>
            ))}
          </div>

          {totalRolePages > 1 && (
            <div className="rbac-pagination">
              <span className="rbac-pagination-info">Page {rolePage} of {totalRolePages} ({filteredRoles.length} Roles)</span>
              <div className="rbac-pagination-actions">
                <button
                  type="button"
                  onClick={() => setRolePage((p) => Math.max(1, p - 1))}
                  disabled={rolePage === 1}
                  className="btn-secondary"
                  style={{ padding: "6px 14px", cursor: "pointer" }}
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setRolePage((p) => Math.min(totalRolePages, p + 1))}
                  disabled={rolePage === totalRolePages}
                  className="btn-secondary"
                  style={{ padding: "6px 14px", cursor: "pointer" }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Permission Matrix Modal */}
      {selectedRoleForMatrix && (
        <RoleMatrixModal
          role={selectedRoleForMatrix}
          onClose={() => setSelectedRoleForMatrix(null)}
          onSaveSuccess={loadRoles}
        />
      )}

      {/* Audit Logs Modal */}
      {showAuditLogs && (
        <AuditLogsModal onClose={() => setShowAuditLogs(false)} />
      )}

      {/* Role Users Assignment Modal */}
      {selectedRoleForUsers && (
        <RoleUsersModal
          role={selectedRoleForUsers}
          onClose={() => setSelectedRoleForUsers(null)}
          onUpdate={loadRoles}
        />
      )}

      {/* Delete Custom Role Confirmation Modal */}
      {deleteTargetRole && (
        <ConfirmModal
          title="Delete Custom Role"
          message={`Are you sure you want to delete custom role "${deleteTargetRole.name}"?`}
          confirmText="Delete Role"
          type="danger"
          onConfirm={async (password) => {
            try {
              await deleteRole(deleteTargetRole.id, password);
              toast.success("Role deleted successfully");
              await loadRoles();
              setDeleteTargetRole(null);
            } catch (err) {
              const msg = err.response?.data?.message || err.response?.data?.detail || "Failed to delete role: Incorrect password or permission denied";
              toast.error(msg);
              throw new Error(msg);
            }
          }}
          onCancel={() => setDeleteTargetRole(null)}
        />
      )}

      {/* Create Role Modal */}
      {showCreateModal && (
        <Modal
          title="Create New Custom Role"
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          size="lg"
        >
          <form onSubmit={handleCreateRole}>
            <div className="attendance-form-body">

              <div className="form-section-card">
                <div className="section-title-premium">
                  <FaUserShield /> Role Details
                </div>

                <div className="full-row">
                  <label>
                    Role Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    className={fieldErrors.name ? "input-error" : ""}
                    type="text"
                    required
                    placeholder="e.g. auditor, team_lead"
                    value={formName}
                    onChange={(e) => {
                      setFormName(e.target.value);
                      if (fieldErrors.name) {
                        setFieldErrors({ ...fieldErrors, name: null });
                      }
                    }}
                  />
                  {fieldErrors.name && (
                    <div className="field-error">{fieldErrors.name}</div>
                  )}
                </div>
              </div>

              <div className="form-section-card">
                <div className="section-title-premium">
                  <FaDatabase /> Data Scope
                </div>

                <div className="full-row">
                  <label>Default Data Scope Level</label>
                  <Dropdown
                    options={[
                      { label: "Self (Own records only)", value: 1 },
                      { label: "Reportees (Direct & indirect subordinates)", value: 2 },
                      { label: "Department (All employees in same department)", value: 3 },
                      { label: "All (Company-wide unrestricted data access)", value: 4 },
                    ]}
                    value={formScope}
                    onChange={(value) => setFormScope(value)}
                    placeholder="Select Data Scope"
                  />
                </div>
              </div>

              <div className="form-section-card">
                <div className="section-title-premium">
                  <FaStickyNote /> Description
                </div>

                <div className="full-row">
                  <textarea
                    className={fieldErrors.description ? "input-error" : ""}
                    rows={3}
                    placeholder="Describe the responsibilities and access level..."
                    value={formDesc}
                    onChange={(e) => {
                      setFormDesc(e.target.value);
                      if (fieldErrors.description) {
                        setFieldErrors({ ...fieldErrors, description: null });
                      }
                    }}
                  />
                  {fieldErrors.description && (
                    <div className="field-error">{fieldErrors.description}</div>
                  )}
                </div>
              </div>

            </div>

            <div
              className="modal-footer"
              style={{
                marginLeft: -26,
                marginRight: -26,
                marginBottom: -22,
                marginTop: 20,
              }}
            >
              <div className="modal-actions">
                <button type="submit" className="primary" disabled={submitting}>
                  {submitting ? "Creating..." : "Create Role"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={submitting}
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Clone Role Modal */}
      {showCloneModal && (
        <Modal
          title={`Clone Role — ${showCloneModal.name}`}
          onClose={() => {
            setShowCloneModal(null);
            resetForm();
          }}
          size="lg"
        >
          <form onSubmit={handleCloneRole}>
            <div className="attendance-form-body">

              <div className="form-section-card">
                <div className="section-title-premium">
                  <FaClone /> Clone Role Details
                </div>

                <p style={{ fontSize: "0.88rem", color: "#64748b", margin: "0 0 16px" }}>
                  Cloning will duplicate all permission matrix selections and the data visibility
                  scope from <strong>{showCloneModal.name}</strong>.
                </p>

                <div className="full-row">
                  <label>
                    New Role Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    className={fieldErrors.name ? "input-error" : ""}
                    type="text"
                    required
                    placeholder="e.g. senior_manager"
                    value={formName}
                    onChange={(e) => {
                      setFormName(e.target.value);
                      if (fieldErrors.name) {
                        setFieldErrors({ ...fieldErrors, name: null });
                      }
                    }}
                  />
                  {fieldErrors.name && (
                    <div className="field-error">{fieldErrors.name}</div>
                  )}
                </div>
              </div>

              <div className="form-section-card">
                <div className="section-title-premium">
                  <FaStickyNote /> Description
                </div>

                <div className="full-row">
                  <textarea
                    className={fieldErrors.description ? "input-error" : ""}
                    rows={3}
                    placeholder="Optional description for the cloned role..."
                    value={formDesc}
                    onChange={(e) => {
                      setFormDesc(e.target.value);
                      if (fieldErrors.description) {
                        setFieldErrors({ ...fieldErrors, description: null });
                      }
                    }}
                  />
                  {fieldErrors.description && (
                    <div className="field-error">{fieldErrors.description}</div>
                  )}
                </div>
              </div>

            </div>

            <div
              className="modal-footer"
              style={{
                marginLeft: -26,
                marginRight: -26,
                marginBottom: -22,
                marginTop: 20,
              }}
            >
              <div className="modal-actions">
                <button type="submit" className="primary" disabled={submitting}>
                  {submitting ? "Cloning..." : "Clone Role"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={submitting}
                  onClick={() => {
                    setShowCloneModal(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}